import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import { CircularProgress } from "@/components/ui/circular-progress";
import { FieldNotch } from "@/components/ui/field-notch";
import { IconButton } from "@/components/ui/icon-button";
import {
  MUI_FIELD_LABEL,
  MUI_FIELD_LABEL_TRANSITION,
  MUI_FIELD_LABEL_FOCUSED,
  MUI_FIELD_LABEL_PRESS_STAND_DOWN,
  MUI_FIELD_LABEL_RESTING,
  MUI_FIELD_LABEL_RESTING_SMALL,
  MUI_FIELD_CONTROL_ROOT,
  MUI_FIELD_NOTCHED_TRIGGER,
  MUI_FIELD_NOTCHED_TRIGGER_MEDIUM,
  MUI_FIELD_OUTLINE_HOVER_WITHIN,
  MUI_FIELD_OUTLINE_PEER,
  MUI_AUTOCOMPLETE_PANEL,
  MUI_AUTOCOMPLETE_OPTION,
  MUI_MENU_ITEM_SELECTED,
  MUI_MENU_LIST,
  MUI_POPOVER_MARGIN_THRESHOLD,
} from "@/components/ui/mui-field-shape";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { MuiCaretGlyph } from "@/components/ui/mui-select-caret";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { cn } from "@/lib/utils";

// A type-to-narrow picker over a flat list of named rows — the donor's
// `AutocompletePaginated` (`timmy/src/components/fields/AutocompletePaginated`),
// ported as a shared Atlas component because it is shared in the donor: the
// items list's manufacturer filter draws it, and the search island draws the
// same control.
//
// The visual and behavioral facts, read off the donor:
//
//   - MUI's outlined `TextField` inside an `Autocomplete` (`mui-field-shape`),
//     `size='small'` on the items sidebar and MUI's default `medium` on the
//     search island (the `size` prop), with the label where MUI puts an outlined label:
//     **inside the field** until it is focused or holds typed text, and only
//     then shrunk onto the outline. This control holds no value of its own (see
//     below), so at rest the label reads like a placeholder — which is exactly
//     what the source screen shows,
//   - **the label carries a count in parentheses when the caller passes one** —
//     the donor's `placeholder` prop, which its manufacturer filter feeds the
//     number of selected manufacturers, so the field reads "Manufacturer (2)",
//   - `blurOnSelect`: a pick closes the list and clears the typed text, so the
//     control is ready for the next one rather than holding the last,
//   - "Loading" while the options are in flight,
//   - the list is MUI's `Menu`: the field's own width, 48px rows, 16px text.
//
// **A picker, not a value.** The donor's manufacturer filter keeps no selection
// in the field — each pick is *added* to the filter and shown as a chip beside
// it — so this reports the option and holds nothing. That is why the prop is
// `onSelect` rather than `value`/`onChange`.
//
// **The donor paginates, and so does this — since #635.** `AutocompletePaginated`
// reads 100 rows at a time under a name filter, watches the last rendered row
// with an `IntersectionObserver`, and appends the page behind it
// (`AutocompletePaginated.tsx:17,35-39,58-73`). This control took the whole
// list in one response and rendered it: 708 manufacturers against the donor's
// 100.
//
// The three moving parts are props rather than a query, the way the sibling
// `LookupSelectField` has them — the control stays query-agnostic, which is
// the donor's own arrangement (`useGetQuery` is a prop there too). What the
// caller owns: the term the read is keyed on (`onSearchSettled`, 300ms behind
// the box), whether a page is left (`hasMore`), and how to ask for it
// (`onLoadMore`).
//
// **Two filters, and both are MUI's.** The debounced term goes to the server;
// the live term also narrows the rows already in hand, because the donor
// passes no `filterOptions` and so keeps MUI's default local filter
// (`Autocomplete.js`, `createFilterOptions`) running over `inputValue` on the
// keystroke.

/**
 * `useDebounce(filterValue, 300)` — `AutocompletePaginated.tsx:27`. Exported
 * so the tests advance exactly this clock, and **not** the lookup menus' 200
 * (`LOOKUP_SEARCH_DEBOUNCE_MS`): two donor controls, two numbers.
 */
export const AUTOCOMPLETE_SEARCH_DEBOUNCE_MS = 300;

export interface AutocompleteOption {
  id: number;
  name: string;
}

interface AutocompleteFieldProps {
  /** The field label, drawn on the outline. Also the input's accessible name. */
  label: string;
  /**
   * Appended to the label in parentheses when set — the donor's count of what
   * is already applied. Dropped when zero, so an untouched field reads plainly.
   */
  labelCount?: number;
  options: readonly AutocompleteOption[];
  onSelect: (option: AutocompleteOption) => void;
  loading?: boolean;
  /**
   * What the list says when the typed term matches nothing.
   *
   * MUI's own default, because the donor passes no `noOptionsText`
   * (`AutocompletePaginated.tsx:92-106`) and so renders MUI's
   * (`Autocomplete.js:325-332`).
   */
  emptyText?: string;
  /**
   * True while another page of options exists behind the ones in hand — the
   * donor's `data?.hasNextPage` (`AutocompletePaginated.tsx:63`).
   */
  hasMore?: boolean;
  /**
   * Fetch the next page. Called when the last rendered row comes into view,
   * which is the donor's own trigger (`AutocompletePaginated.tsx:58-73`).
   *
   * Optional, and its absence is meaningful: a caller that hands over the
   * whole list — the Add Variant dialog's locked single row, say — pages
   * nothing, and no row is watched.
   */
  onLoadMore?: () => void;
  /**
   * The typed term, 300ms after the last keystroke — the value the *read*
   * behind this field is keyed on.
   *
   * The donor's `useDebounce(filterValue, 300)` feeding
   * `filters: { name: debouncedFilterValue }` (`AutocompletePaginated.tsx:27,37`).
   * 300, not the lookup menus' 200: `CustomSelectWithPagination` is a
   * different control and picks a different number.
   */
  onSearchSettled?: (term: string) => void;
  /**
   * True when the last page failed. Read only by the pager, to bound its
   * retries — this field draws no error notice, and neither does the donor's.
   */
  failed?: boolean;
  /**
   * MUI's field size. `small` (40px) is the items sidebar's — it passes
   * `size='small'` (`items-filter-sidebar.tsx:173-176`); the search island
   * mounts its filters without one and so takes MUI's default `medium`, 56px
   * (`SearchFilters.tsx:28-38`, #895).
   */
  size?: "small" | "medium";
  className?: string;
  "data-testid"?: string;
}

/**
 * The listbox's one-line message row.
 *
 * `AutocompleteLoading` and `AutocompleteNoOptions` are the same style object in
 * MUI — `padding: '14px 16px'`, `color: text.secondary`, both inheriting `body1`
 * from `AutocompletePaper` (`Autocomplete.js:307-332`). They are one shell here
 * because they were two, and drifted: the loading row was corrected to the
 * donor's 52px while its twin, one line below, stayed 2px smaller.
 */
function MessageRow({ children }: { children: React.ReactNode }) {
  return <p className="text-lennar-ink-secondary px-4 py-3.5 text-base">{children}</p>;
}

export function AutocompleteField({
  label,
  labelCount,
  options,
  onSelect,
  loading = false,
  emptyText = "No options",
  hasMore = false,
  onLoadMore,
  onSearchSettled,
  failed = false,
  size = "small",
  className,
  "data-testid": testId,
}: AutocompleteFieldProps) {
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  // **The caret, not the list.** MUI floats an `Autocomplete`'s label and cuts
  // its notch off the `TextField`'s own `focused` state, which is independent
  // of the popup in both directions: Escape closes the list and leaves the
  // input focused with its label still up over an open gap, and a keyboard tab
  // into the field floats the label with no list at all — `openOnFocus`
  // defaults to `false` (`useAutocomplete.js:115`) and the donor does not set
  // it (`AutocompletePaginated.tsx:92`). Keying the notch off `open` got both
  // of those wrong.
  const [focused, setFocused] = useState(false);
  const notched = focused || term !== "";
  // Which option the keyboard is on, by index. `-1` is "none yet", which is
  // where every open and every keystroke puts it: MUI highlights nothing until
  // an arrow key is pressed, so Enter on a freshly typed term does nothing
  // rather than picking whatever happened to sort first.
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  // MUI's `firstFocus` (`useAutocomplete.js:137`), which is what makes
  // `selectOnFocus` fire once per visit rather than on every press: the first
  // click after a focus selects the term, and a second click inside it puts
  // the caret where the user aimed. Reset on blur (`:996`).
  const firstFocus = useRef(true);
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);

  // The highlighted option is scrolled into view: the menu caps at 500px and
  // the manufacturer list runs to hundreds of rows, so a walk that leaves the
  // viewport behind is a walk into nowhere.
  useEffect(() => {
    if (activeIndex < 0) return;
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  /**
   * The term the read behind this field is keyed on, 300ms behind the box.
   *
   * The donor's `useDebounce(filterValue, 300)` → `filters: { name: ... }`
   * (`AutocompletePaginated.tsx:27,37`). Reported through a ref for the
   * reason `lookup-select-field.tsx` records at length: the event is *the
   * term settling*, so the term is the only dependency, and listing a
   * caller's fresh lambda instead re-fires on every render.
   */
  const debouncedTerm = useDebouncedValue(term, AUTOCOMPLETE_SEARCH_DEBOUNCE_MS);
  const reportSettled = useRef(onSearchSettled);
  useEffect(() => {
    reportSettled.current = onSearchSettled;
  });
  useEffect(() => {
    reportSettled.current?.(debouncedTerm);
  }, [debouncedTerm]);

  /**
   * The donor's infinite scroll: watch the last rendered row, and fetch the
   * page behind it when it comes into view
   * (`AutocompletePaginated.tsx:58-73`, attached at `:120`).
   *
   * A callback ref, not an effect: the observed node changes every time a page
   * is appended. `new IntersectionObserver(cb)` with no options object, as the
   * donor constructs it. `loading` bails before observing — the donor's
   * `if (isFetching) return` at `:60` — so scrolling on through a loading list
   * does not queue a second page on top of the one already coming.
   *
   * The `askable` latch is the one line that is not the donor's, and it is the
   * bound on retries `lookup-select-field.tsx` explains: `observe()` reports
   * the current state at once and the observer is rebuilt whenever `loading`
   * settles, so a last row still in view asks again the instant a refusal
   * lands. Leaving the foot re-arms it, so a scroll away and back still
   * retries — the donor's recovery kept, its storm dropped.
   */
  const observer = useRef<IntersectionObserver | null>(null);
  const askable = useRef(true);
  useEffect(() => {
    if (!failed) askable.current = true;
  }, [failed]);

  const lastOptionRef = useCallback(
    (node: HTMLLIElement | null) => {
      if (loading) return;
      observer.current?.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (!entry.isIntersecting) {
          askable.current = true;
          return;
        }
        if (!askable.current || !hasMore) return;
        if (failed) askable.current = false;
        onLoadMore?.();
      });
      if (node) observer.current.observe(node);
    },
    [loading, failed, hasMore, onLoadMore],
  );

  // The donor leaks its observer; this one is disconnected on unmount.
  useEffect(() => () => observer.current?.disconnect(), []);

  const labelWithCount = labelCount ? `${label} (${labelCount})` : label;

  // MUI's default `filterOptions`, which the donor keeps by passing none:
  // `matchFrom: 'any'`, `ignoreCase: true`, and **`trim: false`**
  // (`useAutocomplete.js:30-37`). The trim was Atlas's, and it is the same
  // divergence #412's parity review found on the sibling control — "kohler "
  // kept "Kohler" here where the donor's list empties.
  const matches = useMemo(() => {
    const needle = term.toLowerCase();
    if (needle === "") return options;
    return options.filter((option) => option.name.toLowerCase().includes(needle));
  }, [options, term]);

  function pick(option: AutocompleteOption) {
    onSelect(option);
    setActiveIndex(-1);
    // The donor's `blurOnSelect` plus its "clear the filter value upon
    // selection": the field is ready for the next pick, not holding the last.
    setTerm("");
    setOpen(false);
    inputRef.current?.blur();
  }

  return (
    <div
      className={cn("group relative w-full", className)}
      data-testid={testId}
      // MUI's root `handleClick` (`useAutocomplete.js:1128-1137`): a press
      // anywhere in the field puts focus back in the input — which is what
      // makes the caret's `preventDefault` above safe — and, under
      // `selectOnFocus` (default `!freeSolo`, `:120`), selects the term on the
      // first press of a visit. The donor's own guard: only presses inside the
      // control box count, so the helper text below is not part of the field.
      onClick={(event) => {
        if (!anchorRef.current?.contains(event.target as Node)) return;
        const input = inputRef.current;
        if (!input) return;
        input.focus();
        // Never over a selection the press itself made — a drag across the
        // term means the user wanted that range, not all of it.
        if (firstFocus.current && input.selectionEnd === input.selectionStart) input.select();
        firstFocus.current = false;
      }}
    >
      {/* MUI's outlined label, in whichever of its two positions applies. The
          typed term is this field's only "value": with none, and unfocused, the
          label sits inside the field. */}
      <span
        // The two positions are a look, and the browser tests have no
        // stylesheet to measure — so the state is stated here too, which is
        // also what a test would want to read even if it could measure.
        data-shrink={notched}
        className={cn(
          notched
            ? cn(MUI_FIELD_LABEL, focused && MUI_FIELD_LABEL_FOCUSED)
            : size === "medium"
              ? MUI_FIELD_LABEL_RESTING
              : MUI_FIELD_LABEL_RESTING_SMALL,
          // The donor floats this label over 200ms like every other
          // `InputLabel`; it was the one surface that snapped.
          MUI_FIELD_LABEL_TRANSITION,
          MUI_FIELD_LABEL_PRESS_STAND_DOWN,
        )}
      >
        {labelWithCount}
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          {/* MUI's `.MuiOutlinedInput-root`: the input and its adornments, and
              nothing else. `MUI_FIELD_OUTLINE_HOVER_WITHIN` reads its hover from
              here rather than from the field wrapper, because the wrapper
              also holds the label — which floats above the frame and would
              darken it from outside. */}
          <div ref={anchorRef} className={cn(MUI_FIELD_CONTROL_ROOT, "relative")}>
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-label={labelWithCount}
              aria-expanded={open}
              aria-autocomplete="list"
              value={term}
              aria-controls={open ? listId : undefined}
              aria-activedescendant={
                open && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
              }
              onChange={(event) => {
                setTerm(event.target.value);
                setActiveIndex(-1);
                // **Typing opens the list; emptying the box does not.** MUI
                // opens in the `else` of `if (newValue === '')`
                // (`handleInputChange`, `useAutocomplete.js:1022-1030`) — the
                // empty branch clears the value instead. Opening
                // unconditionally popped the list on the Backspace that
                // empties the box, where the donor leaves it shut.
                if (event.target.value !== "") setOpen(true);
              }}
              // **Focus floats the label; the pointer drops the list.** These
              // are two different events in the donor and were one here, so
              // tabbing into the filter popped the whole option list —
              // something modsy never does. `handleFocus` opens only under
              // `openOnFocus` (`useAutocomplete.js:985`), which is off; what
              // opens the list is a left press on the input
              // (`handleInputMouseDown`, `:1138-1144`) or the first keystroke
              // (`handleInputChange`, `:1029`).
              onMouseDown={(event) => {
                if (event.button !== 0) return;
                // MUI's guard and toggle, kept literal
                // (`:1138-1144` into `handlePopupIndicator`, `:1099-1105`): a
                // press re-presses *closed* only while the box is empty. Once
                // something is typed the list stays put, so a press to move
                // the caret within the term cannot dismiss it.
                if (term === "" || !open) setOpen(!open);
              }}
              onFocus={() => setFocused(true)}
              // **`clearOnBlur`**, which defaults to `!freeSolo` and so is on
              // for this field (`useAutocomplete.js:87`). `handleBlur` calls
              // `resetInputValue(event, value, 'blur')` (`:1007-1008`), which
              // puts the box back to the *selected* value's label — and this
              // field never holds one, because `pick` hands the choice up and
              // clears (the donor's "clear the filter value upon selection").
              // So the reset is a clear. Without it, a term typed and
              // abandoned sat in the box keeping the label floated over a
              // filter that was no longer applied.
              //
              // The close and the highlight reset are the same handler's
              // (`handleClose(event, 'blur')`, `:1010`): a list left open over
              // a box that just emptied would be showing the unfiltered
              // options under a filter the user walked away from.
              onBlur={() => {
                setFocused(false);
                firstFocus.current = true;
                setTerm("");
                setActiveIndex(-1);
                setOpen(false);
              }}
              // The keyboard MUI's Autocomplete gives for free, and the donor
              // therefore assumes: the arrows walk the list and wrap at both
              // ends, Enter takes the highlighted option, Escape leaves without
              // one. Wrapping is MUI's behaviour too — `ArrowUp` on a closed or
              // unhighlighted list starts at the *bottom*, which is how a
              // keyboard user reaches the end of a long list in one press.
              onKeyDown={(event) => {
                if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                  event.preventDefault();
                  setOpen(true);
                  if (matches.length === 0) return;
                  const step = event.key === "ArrowDown" ? 1 : -1;
                  setActiveIndex((current) => {
                    // From "nothing highlighted", down starts at the top and up
                    // starts at the bottom.
                    if (current === -1) return step === 1 ? 0 : matches.length - 1;
                    return (current + step + matches.length) % matches.length;
                  });
                  return;
                }
                if (event.key === "Enter") {
                  const option = open ? matches[activeIndex] : undefined;
                  if (!option) return;
                  // Only when it lands on an option: a bare Enter in a filter
                  // field belongs to whatever form the field sits in.
                  event.preventDefault();
                  pick(option);
                  return;
                }
                if (event.key === "Escape") {
                  setActiveIndex(-1);
                  setOpen(false);
                }
              }}
              className={cn(
                size === "medium" ? MUI_FIELD_NOTCHED_TRIGGER_MEDIUM : MUI_FIELD_NOTCHED_TRIGGER,
                "w-full",
                // **43px from the visible right edge, the donor's measured
                // number.** MUI reserves the popup indicator's room on the
                // *root* — `paddingRight: 26 + 4 + 9` with a popup icon — and
                // pads the input `4px` on the right inside it
                // (`Autocomplete.js:139-167`), so 39 + 4. Written less the
                // frame's border for the reason every inset on the shared
                // shape is (`MUI_FIELD_FRAME_BORDER_PUBLISHED`). It was `pr-10`,
                // which with the border read 41 (#576, item G).
                "pr-[calc(43px-var(--mui-field-frame-border))]",
              )}
            />
            {/* **The caret is a button, not decoration.** MUI's popup
                indicator is an `IconButton`
                (`AutocompletePopupIndicator`, `Autocomplete.js:262-282`), and
                the live donor renders it `<button type="button"
                tabindex="-1">` with an `aria-label`/`title` that reads
                `Open` or `Close`. A `pointer-events-none` glyph let the press
                fall through to the input, which opens the list but — once a
                term is typed — will not shut it again
                (`handleInputMouseDown`'s `inputValue === ''` guard), so the
                caret pointed at a close it could not perform. */}
            <IconButton
              // Out of the tab order in the donor: the input is the field's
              // one stop, and the caret duplicates what the arrow keys
              // already do.
              tabIndex={-1}
              aria-label={open ? "Close" : "Open"}
              title={open ? "Close" : "Open"}
              // MUI's root `handleMouseDown` preventDefaults every press that
              // is not on the input itself (`useAutocomplete.js`,
              // `getRootProps`), which is what keeps the caret from stealing
              // focus and blurring the field mid-type.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                // `handlePopupIndicator` (`:1099-1105`) — an *unconditional*
                // toggle, unlike the guarded one the input carries. Measured
                // on the running donor: with `a` typed and the list open, a
                // press shuts it, a second press reopens it, and the term
                // survives both.
                setOpen(!open);
                // The focus comes back via the root's `handleClick` above,
                // which is where the donor puts it — the caret's own
                // `preventDefault` is what it is undoing.
              }}
              // `right-[7px]`, not the glyph's old 12px: MUI puts the
              // `endAdornment` at `right: 9` inside the 1px border and the
              // indicator pulls itself 2px further out (`marginRight: -2`),
              // which measures 7px from the field's right edge on the running
              // donor against Atlas's 10px. The button's own 2px of padding
              // then lands the glyph at the donor's 9px.
              //
              // `p-0.5` is `AutocompletePopupIndicator`'s own `padding: 2`
              // (`Autocomplete.js:262-270`), which MUI sets over
              // `IconButton`'s 8 — so this is a 28px box around the 24px
              // glyph, not the shared component's 40px default. Everything
              // else the donor draws here — the 50% radius, the
              // `action.active` ink, the `rgba(0, 0, 0, 0.04)` hover over
              // `duration.shortest`, the centred ripple and the unclipped
              // hit area — is `IconButton`'s and is no longer spelled out.
              // MUI centres the indicator — `top: calc(50% - 14px)` — so 6px
              // down a 40px field and 14px down a 56px one (measured on the
              // island's Tier filter).
              className={cn(
                "absolute right-[7px] p-0.5",
                size === "medium" ? "top-3.5" : "top-1.5",
              )}
            >
              {/* The **shared** glyph, not a lucide chevron: the donor's popup
                  indicator holds the same `ArrowDropDown` triangle its selects
                  do (measured live — `d="M7 10l5 5 5-5z"`, 24px, inside the
                  28×28 button). `MuiCaretGlyph` rather than `MuiSelectCaret`
                  because this caret's placement belongs to the `IconButton`
                  above, which carries the donor's `right: 9 / marginRight: -2`
                  arithmetic; the positioned variant would fight it.

                  The ink travels with the shared glyph now. It is still the
                  ruled one — the page foreground under `opacity-60`, which the
                  owner approved over the donor's `action.active` (#579, D-D) —
                  so this call site no longer has to restate it, and the
                  guard case below still pins it. */}
              <MuiCaretGlyph open={open} />
            </IconButton>
            {/* The line and the label's hole, last in the root as MUI's
                fieldset is: it reads the input's state as its `peer` and the
                root's hover through `group/control` (`field-notch.tsx`). It
                is handed the same `labelWithCount` the label renders, because
                MUI's legend is a copy of whatever the label's text is —
                a gap sized for "Manufacturer" under a label reading
                "Manufacturer (2)" would leave the count on solid border. */}
            <FieldNotch
              label={labelWithCount}
              notched={notched}
              className={cn(MUI_FIELD_OUTLINE_PEER, MUI_FIELD_OUTLINE_HOVER_WITHIN)}
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          // MUI's `marginThreshold`: the panel stays 16px off every viewport
          // edge rather than opening flush against it (#632, and the note on
          // the constant for the vertical it cannot reproduce).
          collisionPadding={MUI_POPOVER_MARGIN_THRESHOLD}
          // **Flush, the way MUI's popper is.** `Autocomplete.js:599-616`
          // builds its `popper` slot with `disablePortal`, a width and an
          // `anchorEl` and nothing else — no `modifiers`, no `offset` — so
          // `Popper`'s default `bottom` placement puts the paper's top edge on
          // the field's bottom edge. Measured on the running donor: the field
          // bottoms at 457.148 and the paper tops at 457. shadcn's default 4
          // floated the panel clear of the field it belongs to; the sibling
          // selects already spell the donor's zero out for the same reason
          // (`select-field.tsx`).
          sideOffset={0}
          // The list must not steal focus from the input the user is typing in.
          onOpenAutoFocus={(event) => event.preventDefault()}
          // The input is the popover's *anchor*, not its trigger, so Radix
          // counts typing in it as interacting outside and would close the list
          // on the first keystroke. Anything inside the field is inside.
          onInteractOutside={(event) => {
            if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
          }}
          className={MUI_AUTOCOMPLETE_PANEL}
        >
          {/* **MUI's three arms are independent, not a chain.** The loading row
              draws only when there is nothing to show under it (`loading &&
              renderedOptions.length === 0`), the empty row only when the read
              has settled (`renderedOptions.length === 0 && !loading`), and the
              listbox whenever it has options — all three at
              `Autocomplete.js:776-790`. Chaining them on `loading` alone
              collapsed the whole list to the word "Loading" on every refetch,
              and the call site passes `isFetching` (`catalog-filter-sidebar
              .tsx:234,242`), which is true each time the term narrows. The
              donor keeps the current options up while the next read runs. */}
          {loading && matches.length === 0 ? (
            // **The word alone, no spinner.** The donor passes
            // `loadingText={'Loading'}` (`AutocompletePaginated.tsx:105`) and
            // MUI draws that as `AutocompleteLoading` — a plain box with the
            // text, `color: text.secondary`, `padding: 14px 16px`, and no glyph
            // (`Autocomplete.js:316-324,776-779`). The donor's *other* spinner
            // — the one at the foot of the listbox — is a different state and
            // is ported at the bottom of this file: `isLoadingMore` is bound to
            // `isFetching`, not to a page number (`AutocompletePaginated
            // .tsx:106-108`), so it is not the pagination-only affordance its
            // name suggests.
            //
            // `text-base`, not `text-sm`: the box inherits `body1` from
            // `AutocompletePaper` (`Autocomplete.js:307-315`) and the donor's
            // `body1` is 1rem/1.5, a 52px row. At 14px it stood 4px shorter
            // than the donor's and 2px smaller than the option rows directly
            // under it, which take `text-base` from `MUI_AUTOCOMPLETE_OPTION`.
            <MessageRow>Loading</MessageRow>
          ) : null}

          {matches.length === 0 && !loading ? (
            // `AutocompleteNoOptions` is `AutocompleteLoading`'s twin — same
            // `14px 16px`, same `text.secondary`, same inherited `body1`
            // (`Autocomplete.js:325-332`) — so it takes the same treatment as
            // the row above. It was drifting the same 2px/4px, one line away.
            <MessageRow>{emptyText}</MessageRow>
          ) : null}

          {matches.length > 0 ? (
            // A real `listbox` of `option`s, not a list of buttons: the input
            // says `role="combobox"`, and a combobox that points
            // `aria-activedescendant` at something which is not an option
            // leaves a screen reader with nothing to announce.
            <ul
              role="listbox"
              id={listId}
              aria-label={labelWithCount}
              // **"Prevent blur"**, in MUI's own words
              // (`getListboxProps`, `useAutocomplete.js:1248-1255`). A press on
              // an option would otherwise blur the input, and blur now clears
              // the term and shuts the list — which would pull the row out from
              // under the pointer before the click landed on it.
              onMouseDown={(event) => event.preventDefault()}
              // **`MUI_MENU_LIST`'s 8px, and here it is `List`'s rather than
              // `MenuList`'s.** The donor replaces the listbox slot
              // (`AutocompletePaginated.tsx:106` → `listbox.tsx:11` renders a
              // plain MUI `<List>`), and a `List` with `disablePadding` unset
              // is `padding: 8px 0` — measured live on the Manufacturer
              // picker. It rides the call site, not the paper: the paper is
              // the scroll container, and 8px there would leave a band that
              // scrolled rows paint through (see `MUI_MENU_SURFACE`).
              //
              // `p-0` first so the horizontal stays zero, and it is the
              // *list's* zero: the rows' own 16px is on
              // `MUI_AUTOCOMPLETE_OPTION`, where the owner's approved
              // divergence put it, and repeating it here would double it.
              // The donor has neither — its rows run flush to the paper's
              // left edge.
              className={cn("m-0 list-none p-0", MUI_MENU_LIST)}
            >
              {matches.map((option, index) => (
                <li
                  key={option.id}
                  // The donor's `ref={index === options.length - 1 ? ... }`
                  // (`AutocompletePaginated.tsx:120`) — the last row of what
                  // is *rendered*, so a narrowed list pages on its own last
                  // row. Only when the caller pages at all.
                  ref={
                    index === matches.length - 1 && onLoadMore !== undefined
                      ? lastOptionRef
                      : undefined
                  }
                  id={`${listId}-${index}`}
                  role="option"
                  // **False on every row, and that is the donor's answer too.**
                  // `aria-selected` means "this is the chosen value"; the
                  // keyboard highlight is carried by `aria-activedescendant`
                  // on the input above and by the tint. This tracked
                  // `activeIndex`, so a user arrowing down a ten-row list
                  // heard ten rows in turn claim to be the selection when
                  // nothing had been committed (#629).
                  //
                  // Nothing is ever selected *here*: the donor's
                  // `ManufacturersFilter` passes no `value` to
                  // `AutocompletePaginated`
                  // (`ManufacturersFilter.tsx:30-40`), so its `value = null`
                  // default holds and MUI reports `aria-selected="false"` on
                  // every option (`useAutocomplete.js:1278,1290`). The picked
                  // manufacturers live in the chip list below the field, not
                  // as a selection inside it.
                  aria-selected={false}
                  onClick={() => pick(option)}
                  onPointerMove={() => setActiveIndex(index)}
                  // The two conditional halves of the affordance set
                  // `MUI_AUTOCOMPLETE_OPTION`'s doc names: `cursor-pointer`
                  // and the active-row tint. Both are Atlas's rather than the
                  // donor's — the replaced listbox slot took its interactive
                  // rules with it — and both ride the call site rather than
                  // the constant because Tailwind v4 scans source text, so a
                  // variant interpolated onto a shared string emits nothing.
                  // They are a named parity break the owner ruled kept on
                  // #542 (**D3 = a**, 2026-09-22), stated whole beside the
                  // hover tint in `mui-field-shape.ts`.
                  className={cn(
                    MUI_AUTOCOMPLETE_OPTION,
                    "cursor-pointer",
                    index === activeIndex && MUI_MENU_ITEM_SELECTED,
                  )}
                >
                  {option.name}
                </li>
              ))}

              {/* **The donor's second spinner, and it is not a pagination
                  affordance.** `Listbox` draws a centred `CircularProgress
                  size={24}` under the options whenever `isLoadingMore` is set
                  (`listbox.tsx:12-18`), and the call site binds that to
                  `isFetching` (`AutocompletePaginated.tsx:106-108`) — so the
                  donor shows options *and* a foot spinner for exactly the state
                  the arms above now keep the list up for. Atlas reaches it on
                  every warm remount and every lookup invalidation:
                  `useCatalogLookups` runs `staleTime: 0, refetchOnMount:
                  "always"` (`catalog-admin.ts:82-83`), so `isFetching` goes
                  true with the previous options still in hand. Without this the
                  list is silently replaced under the reader.

                  `MuiListItem`'s own box: `flex`, `8px 16px`, centred here by
                  the donor's inline `justifyContent` (`ListItem.js:53-83`). No
                  `color` prop, so it is `primary` — Lennar blue, like the
                  variant tile's. */}
              {loading ? (
                <li
                  data-slot="autocomplete-loading-more"
                  className="flex w-full items-center justify-center px-4 py-2"
                >
                  <CircularProgress size={24} />
                </li>
              ) : null}
            </ul>
          ) : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}
